import { useLocale } from '@app/core/hooks/useLocale';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance } from '@app/shared/components/button/button-enums';
import {
  calculatedMultipleValueFormatterProps,
  getCalculatedProjectValueInPercent,
  numberValueFormatter,
} from '@app/shared/formatters';
import {
  DenominationMultiplier,
  InstrumentBlockKey,
  InstrumentType,
  OwnerType,
} from '@app/shared/models/contracts/enums/shared-enums';
import classNames from 'classnames';
import { FC, Fragment } from 'react';
import styles from '@app/modules/projects/dashboard/shared/per-year-capital-structure/per-year-capital-structure-chart.module.scss';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { Alignment } from '@app/shared/enums/alignment.enum';
import {
  amountRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import {
  PerYearCapitalStructureChartInstrument,
  PerYearCapitalStructureChartInstrumentOwnership,
} from '@app/modules/projects/dashboard/shared/per-year-capital-structure/per-year-capital-structure-chart-types';
import { enumKeyByValue, instrumentOwnersSortFn } from '@app/shared/helpers';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import SvgChevron from '@app/shared/icons/Chevron';

interface InstrumentsDataDto extends PerYearCapitalStructureChartInstrument {
  rank?: number;
}

interface PerYearCapitalStructureInstrumentTableSectionProps {
  instrumentsType: InstrumentType | null;
  instrumentsData: InstrumentsDataDto[];
  isEntryStack: boolean;
  setExpandedInstruments: (expandedInstruments: Dictionary<boolean>) => void;
  expandedInstruments: Dictionary<boolean>;
  selectedBlock: string | null;
  shouldScaleDenomination?: boolean;
  percentageBasis?: 'netEv' | 'netEquity';
}

export const PerYearCapitalStructureInstrumentTableSection: FC<
  PerYearCapitalStructureInstrumentTableSectionProps
> = ({
  instrumentsType,
  instrumentsData,
  isEntryStack = false,
  setExpandedInstruments,
  expandedInstruments,
  selectedBlock,
  shouldScaleDenomination = true,
  percentageBasis = 'netEv',
}) => {
  const { l } = useLocale();

  const denomination = useAppSelector((state) => state.project.projectDraft.details.denomination);
  const multiplier = shouldScaleDenomination ? DenominationMultiplier[denomination] : 1;

  const getTitle = () => {
    switch (instrumentsType) {
      case InstrumentType.ShareholderLoanNotes:
        return l('_ShareholderLoanNotesTitle');
      case InstrumentType.PreferredShares:
        return l('_PreferredShares');
      case InstrumentType.OrdinaryEquity:
        return l('_OrdinaryEquityTitle');
      default:
        return l('_NetDebtOrNetCashNegative');
    }
  };

  const getIconClassSuffix = (isSweet: boolean | undefined) => {
    switch (instrumentsType) {
      case InstrumentType.ShareholderLoanNotes:
        return 'sln';
      case InstrumentType.PreferredShares:
        return 'pref';
      case InstrumentType.OrdinaryEquity:
        return isSweet ? 'sweet' : 'ord';
      default:
        return 'net-debt';
    }
  };

  const renderProceedsByOwner = (
    proceedsByOwner: Dictionary<PerYearCapitalStructureChartInstrumentOwnership>,
    selectedBlock: string | null,
    instrumentId: string
  ) => {
    return Object.entries(proceedsByOwner)
      .sort((a, b) =>
        instrumentOwnersSortFn(a[0] as keyof typeof OwnerType, b[0] as keyof typeof OwnerType)
      )
      .map(([owner, data]) => (
        <tr
          key={owner}
          className={classNames(styles['modal-instrument'], styles['italic-text'], {
            [styles['modal-instrument--faded']]: selectedBlock && selectedBlock !== instrumentId,
          })}>
          <td className={classNames(styles['no-padding-top'], styles['modal-proceeds-title'])}>
            {owner === enumKeyByValue(OwnerType, OwnerType.Institution) ? l('_Sponsor') : owner}
          </td>
          <td className={styles['no-padding-top']}>
            <CellValue value={data.proceeds * multiplier} {...amountRules} isAbbreviated />
          </td>
          <td className={styles['no-padding-top']}>
            <CellValue
              value={getCalculatedProjectValueInPercent(data.percentageOfNetExitProceeds)}
              {...enteredPercentageViewRules}
              alignment={Alignment.Right}
            />
          </td>
          {!isEntryStack && (
            <>
              <td />
              <td />
            </>
          )}
        </tr>
      ));
  };

  return (
    <>
      <tr className={styles['modal-instrument-type-title']}>
        <td
          className={classNames(styles['table-subtitle'], styles['no-padding-left'])}
          data-testid="instrument-title">
          {getTitle()}
        </td>
      </tr>
      {instrumentsData
        .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
        .map((instrument) => (
          <Fragment key={instrument.id}>
            <tr
              data-testid={`instrument-${instrument.id}`}
              className={classNames(styles['modal-instrument'], {
                [styles['modal-instrument--faded']]:
                  selectedBlock && selectedBlock !== instrument.id,
              })}>
              <td
                className={classNames(
                  styles['modal-instrument-title'],
                  styles['no-padding-left'],
                  'table-primary'
                )}>
                <SvgChevron
                  onClick={(event) => {
                    event.stopPropagation();
                    setExpandedInstruments({
                      ...expandedInstruments,
                      [instrument.id]: !expandedInstruments[instrument.id],
                    });
                  }}
                  className={classNames(styles['chevron'], {
                    [styles['chevron__collapsed']]: !expandedInstruments[instrument.id],
                    [styles['chevron__expanded']]: expandedInstruments[instrument.id],
                    [styles['chevron__hidden']]: instrument.id === InstrumentBlockKey.NetDebtId,
                  })}
                />
                <Button
                  appearance={ButtonAppearance.CLEAN}
                  cursorDefault
                  ignoreReadOnly
                  className={styles['icon--button']}>
                  <span
                    className={classNames([
                      styles['icon'],
                      styles[`icon--${getIconClassSuffix(instrument.isSweet)}`],
                    ])}
                  />
                  {instrument.id === InstrumentBlockKey.NetDebtId
                    ? l('_NetDebtOrNetCashNegative')
                    : instrument.narrative ?? ''}
                </Button>
              </td>
              <td>
                <CellValue
                  value={instrument.totalProceeds * multiplier}
                  {...amountRules}
                  isAbbreviated
                />
              </td>
              <td>
                <CellValue
                  value={getCalculatedProjectValueInPercent(
                    percentageBasis === 'netEquity'
                      ? instrument.totalProceedsPercentageOfTotalEquityProceeds
                      : instrument.totalProceedsPercentageOfNetExitProceeds
                  )}
                  {...enteredPercentageViewRules}
                  alignment={Alignment.Right}
                />
              </td>
              {!isEntryStack && (
                <>
                  {instrument.id !== InstrumentBlockKey.NetDebtId ? (
                    <>
                      <td>
                        <CellValue
                          value={numberValueFormatter({
                            value: instrument.instrumentMoM,
                            ...calculatedMultipleValueFormatterProps,
                          })}
                          alignment={Alignment.Right}
                        />
                      </td>
                      <td>
                        <CellValue
                          value={getCalculatedProjectValueInPercent(instrument.instrumentIRR)}
                          {...enteredPercentageViewRules}
                          alignment={Alignment.Right}
                        />
                      </td>
                    </>
                  ) : (
                    <>
                      <td />
                      <td />
                    </>
                  )}
                </>
              )}
            </tr>
            {expandedInstruments[instrument.id] &&
              renderProceedsByOwner(instrument.proceedsByOwner, selectedBlock, instrument.id)}
          </Fragment>
        ))}
    </>
  );
};
